import React, {useMemo} from "react";
import {DealType} from "../domain/DealSelection.model";
import {HoverPopoverUI} from "../../common/ui/HoverPopoverUI";
import InfoIcon from '@mui/icons-material/Info';
import {spanishDateFormatter} from "../../ui/components/SpanishDateFormatter";
import {InfoLineUI} from "./InfoLineUI";
import {FormattedNumber} from "../../common/ui/FormattedNumber";
import {Price} from "../../common/ui/Price";

/**
 *
 * @param {{
 *     dealData: {
 *          offers: [],
 *          promos: [],
 *          lots: [],
 *          hasDealsToChoose: boolean,
 *     }
 *     selection: {type: DealType, value: any},
 *     onSelectionChange: (selection: {type: DealType, value: any}) => void,
 * }} props
 */
export const DealSelectorBis = ({dealData, selection, onSelectionChange}) => {
    const handleDealSelection = (offer) =>
        onSelectionChange({type: DealType.OFFER, value: offer});

    const offerLines = useMemo(
        () => buildOfferLines(dealData.offers, selection?.value, handleDealSelection),
        [dealData.offers, selection?.value]
    );

    const handlePromoSelection = (promo) =>
        onSelectionChange({type: DealType.PROMO, value: promo});

    const promoLines = useMemo(
        () => buildPromoLines(dealData.promos, selection?.value, handlePromoSelection),
        [dealData.promos, selection?.value]
    );

    const handleLotSelection = (lot) =>
        onSelectionChange({type: DealType.LOT, value: lot});

    const lotLines = useMemo(
        () => buildLotLines(dealData.lots, selection?.value, handleLotSelection),
        [dealData.lots, selection?.value]
    );

    return (
        <div className="fw-lighter d-flex flex-column gap-2">
            {renderLines("Ofertas exclusivas", offerLines)}
            {renderLines("Promociones con obsequios", promoLines)}
            {renderLines("Promociones por lotes", lotLines)}

            {dealData.hasDealsToChoose && (
                <div className="ms-3 mt-2 d-flex flex-row gap-3 align-items-center">
                    <input
                        type="radio"
                        name="noDeal"
                        className="form-check-input m-0"
                        checked={!selection}
                        onChange={(event) => {
                            event.target.checked && onSelectionChange(null);
                        }}
                    />
                    <strong>No quiero ninguna promoción</strong>
                </div>
            )}
        </div>
    );
};

function renderLines(title, lines) {
    if (!lines?.length) return null;
    return (
        <>
            {lines.map((line, idx) => (
                <InfoLineUI
                    key={line.key}
                    title={idx === 0 ? title : undefined}
                    leading={line.leading}
                    items={line.items}
                />
            ))}
        </>
    );
}

const buildPrice = (price) => {
    return (
        <Price data={{sellingPrice: price.bestPrice, basePrice: price.value}} config={{size: "small"}} />
    );
}

const buildOfferLines = (offers = [], selected, onSelect) =>
    offers.map((offer, idx) => {
        const isSelected = selected === offer;

        const leading = (
            <div className="d-flex align-items-center">
                <input
                    type="radio"
                    name="offer"
                    className="form-check-input m-0"
                    checked={isSelected}
                    onChange={() => onSelect(offer)}
                />
            </div>
        );

        const disc = <FormattedNumber value={offer.price.discount} config={{style: "percent"}} />;

        const items = [
            {key: "price", label: "", value: buildPrice(offer.price)},
            {key: "discount", label: "Desc.", value: disc},
            {key: "minQty", label: "Cant. mín.", value: `${offer.minQuantity} Uds`},
        ];

        if (offer.conditions?.trim()) {
            items.push({
                key: "conditions",
                label: "Condiciones",
                value: (
                    <HoverPopoverUI message={offer.conditions}>
                        <InfoIcon className="text-warning" style={{cursor: "pointer", width: "2rem"}}/>
                    </HoverPopoverUI>
                ),
            });
        }

        return {key: `offer-${idx}`, leading, items};
    });

const buildPromoLines = (promos = [], selected, onSelect) =>
    promos.map((promo, idx) => {
        const isSelected = selected === promo;

        const leading = (
            <div className="d-flex align-items-center">
                <input
                    type="radio"
                    name="promo"
                    className="form-check-input m-0"
                    checked={isSelected}
                    onChange={() => onSelect(promo)}
                />
            </div>
        );

        const price = <FormattedNumber value={promo.packPrice} config={{style: "currency"}} />;

        const items = [
            {key: "price", label: "", value: price },
            {key: "desc", label: "Descripción", value: promo.description},
            {key: "min", label: "Cant. mín.", value: `${promo.minPurchaseForPromo} Uds`},
            {key: "gift", label: "Precio obsequio", value: buildPrice(promo.price)},
        ];

        return {key: `promo-${idx}`, leading, items};
    });

const buildLotLines = (lots = [], selected, onSelect) =>
    lots.map((lot, idx) => {
        const isSelected = selected === lot;

        const leading = (
            <div className="d-flex align-items-center">
                <input
                    type="radio"
                    name="lot"
                    className="form-check-input m-0"
                    checked={isSelected}
                    onChange={() => onSelect(lot)}
                />
            </div>
        );

        const items = [
            {key: "price", label: "", value: buildPrice(lot.price)},
            {key: "discount", label: "Desc.", value: lot.price.discountLine.replace('.', ',')},
            {key: "stock", label: "Stock lote:", value: String(lot.stock)},
            {key: "exp", label: "Caducidad:", value: spanishDateFormatter(lot.expirationDate)},
        ];

        return {key: `lot-${idx}`, leading, items};
    });
